import { isUndefined, noop } from 'lodash';
import {
  createContext,
  memo,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import { reportErrorToBugsnag } from '@air/utils-error';

import { useResetPeoplePanelStateOnClipChange } from '~/components/AssetModalV2/Content/Panel/contexts/hooks/useResetPeoplePanelStateOnClipChange';
import { getPendingDraftFaces } from '~/components/AssetModalV2/hooks/getPendingDraftFaces';
import { type PercentageRect } from '~/components/BoundingBox/types';
import { clearPendingClipPersonIdRemap } from '~/hooks/people/clipPersonSelectionRemap';
import { useAutoClearSelectedPersonOnTabClose } from '~/hooks/people/useAutoClearSelectedPersonOnTabClose';
import { clearAllSelectedPersonIds } from '~/hooks/people/useSelectedPersonId';
import { centralizedClipIdSelector } from '~/store/centralizedClip/selectors';
import {
  type ClipFace,
  type ClipFaceAbsoluteDimensions,
  type ClipFaceThumbnailRegion,
} from '~/swr-hooks/clips/faces/types';
import { isDraftFaceId } from '~/swr-hooks/clips/faces/utils';
import { isDevOrTestStage } from '~/swr-hooks/utils';

// Draft faces use empty region placeholder for ClipFace compat; consumers gate on hasFaceRegion.
const EMPTY_DRAFT_REGION: ClipFaceThumbnailRegion = {
  left: 0,
  top: 0,
  width: 0,
  height: 0,
  absoluteWidth: 0,
  absoluteHeight: 0,
};

export const percentageRectToClipFaceRegion = (
  rect: PercentageRect,
  imageWidth: number,
  imageHeight: number,
): ClipFaceThumbnailRegion => {
  const left = rect.pctStartX;
  const top = rect.pctStartY;
  const width = rect.pctEndX - rect.pctStartX;
  const height = rect.pctEndY - rect.pctStartY;

  // Box-pixel (not image-pixel) dims: `FacePersonThumbnail` recovers the
  // image width via the saved-face convention `totalWidth = absoluteWidth
  // / width`, so drafts need box-pixel `absoluteWidth/Height` for the
  // formula to hold.
  const absoluteWidth = Math.round(width * imageWidth);
  const absoluteHeight = Math.round(height * imageHeight);

  return {
    left,
    top,
    width,
    height,
    absoluteWidth,
    absoluteHeight,
  };
};

/** Recover full image dimensions from a normalized region (saved-face convention). */
export const imageDimensionsFromClipFaceRegion = (region: ClipFaceAbsoluteDimensions) => ({
  width: region.width > 0 ? Math.round(region.absoluteWidth / region.width) : 0,
  height: region.height > 0 ? Math.round(region.absoluteHeight / region.height) : 0,
});

export type PeoplePanelContextValue = {
  draftFaces: ClipFace[];
  autoFocusFaceId: string | null;
  addDraftPerson: (region?: ClipFaceThumbnailRegion) => string;
  updateDraftFaceRegion: (faceId: string, region: ClipFaceThumbnailRegion) => void;
  removeDraftFace: (faceId: string) => void;
  getDraftFace: (faceId: string) => ClipFace | undefined;
  clearAutoFocusFaceId: () => void;
  mergeWithDraftFaces: (apiFaces: ClipFace[]) => ClipFace[];
  // Discards the draft only when it's unnamed — covers both regionless
  // "Tag person" rows and in-progress draws so Esc/click-away can cancel.
  discardUnnamedDrawnDraftFace: (faceId: string) => boolean;
  // Set when the panel-row "Add bounding box" menu item is clicked; read by
  // the visualizer's `onBoundingBoxDrawn` to attach the next drawn rectangle
  // to the existing clip-person instead of creating a new draft face.
  pendingClipPersonBoundingBoxId: string | null;
  setPendingClipPersonBoundingBoxId: (id: string | null) => void;
};

const defaultContextValue: PeoplePanelContextValue = {
  draftFaces: [],
  autoFocusFaceId: null,
  addDraftPerson: () => '',
  updateDraftFaceRegion: noop,
  removeDraftFace: noop,
  getDraftFace: () => undefined,
  clearAutoFocusFaceId: noop,
  mergeWithDraftFaces: (apiFaces) => apiFaces,
  discardUnnamedDrawnDraftFace: () => false,
  pendingClipPersonBoundingBoxId: null,
  setPendingClipPersonBoundingBoxId: noop,
};

export type PeoplePanelContextProviderProps = PropsWithChildren;

const PeoplePanelContext = createContext<PeoplePanelContextValue>(defaultContextValue);

export const PeoplePanelContextProvider = memo(({ children }: PeoplePanelContextProviderProps) => {
  const clipId = useSelector(centralizedClipIdSelector);
  const [draftFaces, setDraftFaces] = useState<ClipFace[]>([]);
  const [autoFocusFaceId, setAutoFocusFaceId] = useState<string | null>(null);
  const [pendingClipPersonBoundingBoxId, setPendingClipPersonBoundingBoxId] = useState<string | null>(null);

  const resetPeoplePanelStateOnClipChange = useCallback(() => {
    setDraftFaces([]);
    setAutoFocusFaceId(null);
    setPendingClipPersonBoundingBoxId(null);
    clearPendingClipPersonIdRemap();
    clearAllSelectedPersonIds();
  }, []);

  useResetPeoplePanelStateOnClipChange(clipId, resetPeoplePanelStateOnClipChange);

  useEffect(() => {
    return () => {
      clearPendingClipPersonIdRemap();
    };
  }, []);

  const addDraftPerson = useCallback((region: ClipFaceThumbnailRegion = EMPTY_DRAFT_REGION): string => {
    const id = `draft-${crypto.randomUUID()}`;
    const draftFace: ClipFace = { id, person: undefined, region };

    setDraftFaces((current) => [...current, draftFace]);
    setAutoFocusFaceId(id);

    return id;
  }, []);

  const updateDraftFaceRegion = useCallback((faceId: string, region: ClipFaceThumbnailRegion) => {
    setDraftFaces((current) => current.map((face) => (face.id === faceId ? { ...face, region } : face)));
  }, []);

  const removeDraftFace = useCallback((faceId: string) => {
    setDraftFaces((current) => current.filter((face) => face.id !== faceId));
  }, []);

  const getDraftFace = useCallback((faceId: string) => draftFaces.find((face) => face.id === faceId), [draftFaces]);

  const clearAutoFocusFaceId = useCallback(() => {
    setAutoFocusFaceId(null);
  }, []);

  const mergeWithDraftFaces = useCallback(
    (apiFaces: ClipFace[]) => [...apiFaces, ...getPendingDraftFaces(apiFaces, draftFaces)],
    [draftFaces],
  );

  const discardUnnamedDrawnDraftFace = useCallback(
    (faceId: string): boolean => {
      if (!isDraftFaceId(faceId)) {
        return false;
      }

      const draft = draftFaces.find((face) => face.id === faceId);

      if (!draft || !isUndefined(draft.person)) {
        return false;
      }

      setDraftFaces((current) => current.filter((face) => face.id !== faceId));
      return true;
    },
    [draftFaces],
  );

  const clearPeoplePanelTransientState = useCallback(() => {
    setAutoFocusFaceId(null);
    setPendingClipPersonBoundingBoxId(null);
  }, []);

  useAutoClearSelectedPersonOnTabClose({ onPeopleUiClose: clearPeoplePanelTransientState });

  const value = useMemo<PeoplePanelContextValue>(
    () => ({
      draftFaces,
      autoFocusFaceId,
      addDraftPerson,
      updateDraftFaceRegion,
      removeDraftFace,
      getDraftFace,
      clearAutoFocusFaceId,
      mergeWithDraftFaces,
      discardUnnamedDrawnDraftFace,
      pendingClipPersonBoundingBoxId,
      setPendingClipPersonBoundingBoxId,
    }),
    [
      addDraftPerson,
      autoFocusFaceId,
      clearAutoFocusFaceId,
      draftFaces,
      getDraftFace,
      mergeWithDraftFaces,
      pendingClipPersonBoundingBoxId,
      removeDraftFace,
      updateDraftFaceRegion,
      discardUnnamedDrawnDraftFace,
    ],
  );

  return <PeoplePanelContext.Provider value={value}>{children}</PeoplePanelContext.Provider>;
});

PeoplePanelContextProvider.displayName = 'PeoplePanelContextProvider';

export const usePeoplePanelContext = (): PeoplePanelContextValue => {
  const context = useContext(PeoplePanelContext);

  if (context === defaultContextValue) {
    const error = new Error('usePeoplePanelContext must be used within PeoplePanelContextProvider');

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({ error, context: 'PeoplePanel context not found' });
  }

  return context;
};
